import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { useAuth } from "@/hooks/use-auth";
import { Button } from "@/components/ui/button";
import { Heart, ShoppingCart, Trash2 } from "lucide-react";
import { Link } from "react-router-dom";
import { formatBHD } from "@/lib/utils";
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";

interface WishlistProduct {
  id: string;
  name: string;
  price: number;
  description?: string;
  category?: string;
  badge?: string;
  rating?: number;
  images: Array<{ url: string; position: number }>;
}

interface WishlistItemWithProduct {
  id: string;
  created_at: string;
  product: WishlistProduct;
}

const Wishlist = () => {
  const { user } = useAuth();
  const [wishlistItems, setWishlistItems] = useState<WishlistItemWithProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch wishlist with product details
  useEffect(() => {
    if (!user) {
      setLoading(false);
      return;
    }

    async function fetchWishlist() {
      try {
        setLoading(true);
        setError(null);

        const { data, error: fetchError } = await supabase
          .from('wishlists')
          .select(`
            id,
            created_at,
            products (
              id,
              name,
              price,
              description,
              short_description,
              featured,
              product_images (
                url,
                position,
                alt_text
              )
            )
          `)
          .eq('user_id', user.id)
          .order('created_at', { ascending: false });

        if (fetchError) throw fetchError;

        // Transform data
        const transformedItems: WishlistItemWithProduct[] = (data || [])
          .filter(item => item.products) // Filter out items with deleted products
          .map(item => ({
            id: item.id,
            created_at: item.created_at,
            product: {
              id: (item.products as any).id,
              name: (item.products as any).name,
              price: (item.products as any).price,
              description: (item.products as any).description || (item.products as any).short_description,
              category: 'General',
              badge: (item.products as any).featured ? 'Featured' : undefined,
              rating: 4.5,
              images: ((item.products as any).product_images || [])
                .sort((a: any, b: any) => a.position - b.position)
                .map((img: any) => ({
                  url: img.url,
                  position: img.position,
                })),
            },
          }));

        setWishlistItems(transformedItems);
      } catch (err) {
        console.error('Error fetching wishlist:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch wishlist');
      } finally {
        setLoading(false);
      }
    }

    fetchWishlist();
  }, [user]);

  // Remove from wishlist handler
  const handleRemoveFromWishlist = async (wishlistItemId: string) => {
    try {
      const { error } = await supabase
        .from('wishlists')
        .delete()
        .eq('id', wishlistItemId);

      if (error) throw error;

      // Update local state
      setWishlistItems(prev => prev.filter(item => item.id !== wishlistItemId));
    } catch (err) {
      console.error('Error removing from wishlist:', err);
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navigation />
        <main className="flex-1 flex items-center justify-center bg-gradient-to-br from-background via-background to-muted/20">
          <div className="text-center max-w-md px-4">
            <Heart className="h-24 w-24 mx-auto text-muted-foreground mb-6" />
            <h1 className="text-3xl font-bold mb-4">Login to View Wishlist</h1>
            <p className="text-muted-foreground mb-6">
              Save your favorite products and shop them later
            </p>
            <Button asChild size="lg" className="bg-gradient-to-r from-brand-cyan via-brand-magenta to-brand-yellow text-white">
              <Link to="/login">Login to Continue</Link>
            </Button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navigation />
        <main className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4" />
            <p className="text-muted-foreground">Loading your wishlist...</p>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (wishlistItems.length === 0) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navigation />
        <main className="flex-1 flex items-center justify-center bg-gradient-to-br from-background via-background to-muted/20">
          <div className="text-center max-w-md px-4">
            <Heart className="h-24 w-24 mx-auto text-muted-foreground mb-6" />
            <h1 className="text-3xl font-bold mb-4">Your Wishlist is Empty</h1>
            <p className="text-muted-foreground mb-6">
              Start adding products you love and save them for later!
            </p>
            <Button asChild size="lg" className="bg-gradient-to-r from-brand-cyan via-brand-magenta to-brand-yellow text-white">
              <Link to="/shop">Browse Products</Link>
            </Button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Navigation />
      <main className="flex-1">
        {/* Hero Header */}
        <div className="bg-gradient-to-r from-red-600 via-pink-600 to-purple-600 py-16">
          <div className="container mx-auto px-4">
            <div className="flex items-center justify-center gap-3 mb-4">
              <Heart className="h-12 w-12 text-white fill-white" />
              <h1 className="text-4xl md:text-5xl font-bold text-white">
                My Wishlist
              </h1>
            </div>
            <p className="text-white/90 text-lg text-center">
              {wishlistItems.length} {wishlistItems.length === 1 ? "item" : "items"} saved for later
            </p>
          </div>
        </div>

        {/* Wishlist Grid */}
        <div className="container mx-auto px-4 py-12">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {wishlistItems.map(({ product, id }) => {
              const primaryImage = product.images && product.images.length > 0
                ? product.images[0].url
                : '/placeholder-product.png';

              return (
                <div
                  key={id}
                  className="bg-card rounded-lg shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-300 group"
                >
                  <div className="relative overflow-hidden">
                    <img
                      src={primaryImage}
                      alt={product.name}
                      className="w-full h-64 object-cover group-hover:scale-110 transition-transform duration-500"
                      onError={(e) => {
                        e.currentTarget.src = '/placeholder-product.png';
                      }}
                    />

                    {/* Remove Button */}
                    <button
                      onClick={() => handleRemoveFromWishlist(id)}
                      className="absolute top-3 right-3 p-2 bg-white rounded-full shadow-lg hover:bg-red-50 transition-colors"
                      title="Remove from wishlist"
                    >
                      <Heart className="h-5 w-5 fill-red-500 text-red-500" />
                    </button>

                    {/* Badge */}
                    {product.badge && (
                      <div className="absolute top-3 left-3">
                        <span className="bg-secondary text-secondary-foreground px-3 py-1 rounded-full text-xs font-bold">
                          {product.badge}
                        </span>
                      </div>
                    )}
                  </div>

                  <div className="p-4">
                    {product.category && (
                      <div className="text-xs text-muted-foreground uppercase tracking-wider mb-2">
                        {product.category}
                      </div>
                    )}
                    <h3 className="font-semibold text-lg mb-3 line-clamp-2">
                      {product.name}
                    </h3>

                    {/* Rating */}
                    {product.rating && (
                      <div className="flex items-center gap-1 mb-3">
                        {[...Array(5)].map((_, i) => (
                          <span
                            key={i}
                            className={`text-sm ${
                              i < Math.floor(product.rating || 0)
                                ? "text-accent"
                                : "text-muted"
                            }`}
                          >
                            ★
                          </span>
                        ))}
                        <span className="text-xs text-muted-foreground ml-1">
                          ({product.rating})
                        </span>
                      </div>
                    )}

                    {/* Price */}
                    <div className="flex items-baseline gap-2 mb-4">
                      <span className="text-2xl font-bold text-primary">
                        {formatBHD(product.price)}
                      </span>
                    </div>

                    {/* Actions */}
                    <div className="flex gap-2">
                      <Button className="flex-1 bg-gradient-to-r from-brand-cyan via-brand-magenta to-brand-yellow text-white">
                        <ShoppingCart className="mr-2 h-4 w-4" />
                        Add to Cart
                      </Button>
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => handleRemoveFromWishlist(id)}
                        title="Remove"
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Wishlist;
